import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";
import camp from "../assets/partners/konopac.png";
import arabela from "../assets/partners/arabela.png";
import bushmanBar from "../assets/partners/bushman-bar.png";
import { stagger, blurUp, rise, ruleGrow } from "../utils/motion";

const headVariants = blurUp;
const gridVariants = stagger(0.14, 0.1);
const cardVariants = rise;

const partners = [
  {
    name: "Konopáč",
    description:
      "Letní kemp uprostřed přírody Železných hor — ideální místo pro firemní teambuildingy a rodinné dovolené.",
    logo: camp,
    url: "https://konopac-hm.cz/",
  },
  {
    name: "Arabela Restaurant",
    description:
      "Tradiční restaurace v areálu kempu Konopáč poblíž Heřmanova Městce. Možnost pronájmu prostor s ubytováním pro vaši akci.",
    logo: arabela,
    url: "https://arabelarestaurant.cz/",
  },
  {
    name: "Bushman Bar Pardubice",
    description:
      "Náš domovský bar v centru Pardubic. Živá scéna, profesionální barman a oblíbená setkávací místa.",
    logo: bushmanBar,
    url: "https://www.bushmanbar.cz/",
  },
];

const Contact = () => {
  return (
    <Wrapper>
      <div className="section-center">
        <motion.header
          className="sec-head"
          variants={headVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.6 }}
        >
          <span className="kicker">
            <motion.span className="rule" aria-hidden="true" variants={ruleGrow} />
            Spolupracujeme s
          </span>
          <h2>
            Naši <em>partneři</em>
          </h2>
        </motion.header>

        <motion.div
          className="partners-grid"
          variants={gridVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          {partners.map((p, idx) => (
            <motion.a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noreferrer noopener"
              className="partner-card"
              variants={cardVariants}
            >
              <span className="index" aria-hidden="true">
                0{idx + 1}
              </span>
              <div className="logo-frame">
                <span className="plaque">
                  <img src={p.logo} alt={p.name} />
                </span>
              </div>
              <div className="body">
                <h3>{p.name}</h3>
                <p>{p.description}</p>
                <span className="cta">
                  Navštívit web
                  <FaExternalLinkAlt aria-hidden="true" />
                </span>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  padding: 6.5rem 0;
  background: var(--clr-cream-warm);

  .sec-head {
    margin-bottom: 3.5rem;
  }
  .kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
    font-family: var(--ff-body);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--clr-accent-dark);
    margin-bottom: 1rem;
  }
  .kicker .rule {
    display: inline-block;
    width: 40px;
    height: 1px;
    background: var(--clr-accent);
    transform-origin: left;
  }
  h2 {
    font-size: clamp(2rem, 4.5vw, 3.1rem);
    letter-spacing: -0.015em;
    margin: 0;
  }
  h2 em {
    font-style: italic;
    font-weight: 500;
    color: var(--clr-accent-dark);
  }

  .partners-grid {
    display: grid;
    gap: 1.75rem;
    grid-template-columns: 1fr;
  }

  .partner-card {
    position: relative;
    background: var(--clr-cream);
    border: 1px solid var(--clr-divider);
    border-radius: var(--radius-lg);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: transform 450ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 450ms ease, box-shadow 450ms ease;
    color: inherit;
    text-decoration: none;
  }
  /* dark ink that floods the card from the bottom on hover */
  .partner-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--clr-dark-2);
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 480ms cubic-bezier(0.22, 1, 0.36, 1);
    z-index: 0;
  }
  .partner-card > * {
    position: relative;
    z-index: 1;
  }
  .partner-card:hover {
    transform: translateY(-6px);
    border-color: var(--clr-dark-2);
    box-shadow: var(--shadow-lg);
  }
  .partner-card:hover::before {
    transform: scaleY(1);
  }
  .partner-card:hover .plaque {
    transform: rotate(-1.5deg) scale(1.05);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  }
  .partner-card:hover .index {
    color: var(--clr-accent-light);
  }
  .partner-card:hover .logo-frame {
    border-bottom-color: rgba(250, 244, 234, 0.16);
  }
  .partner-card:hover .body h3 {
    color: var(--clr-cream);
  }
  .partner-card:hover .body p {
    color: rgba(250, 244, 234, 0.75);
  }
  .partner-card:hover .cta {
    color: var(--clr-accent-light);
    gap: 0.85rem;
  }

  .index {
    position: absolute;
    top: 1rem;
    right: 1.25rem;
    z-index: 1;
    font-family: var(--ff-display);
    font-style: italic;
    font-size: 1.15rem;
    color: var(--clr-charcoal-3);
    transition: color 450ms ease;
  }

  .logo-frame {
    padding: 2rem 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 190px;
    border-bottom: 1px solid var(--clr-divider);
    transition: border-bottom-color 450ms ease;
  }
  .plaque {
    background: var(--clr-paper);
    border: 1px solid var(--clr-divider);
    border-radius: var(--radius-lg);
    width: 230px;
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1.5rem;
    box-shadow: var(--shadow-sm);
    transition: transform 450ms cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow var(--transition);
  }
  .logo-frame img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    display: block;
  }

  .body {
    padding: 1.6rem 1.6rem 1.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .body h3 {
    font-family: var(--ff-display);
    color: var(--clr-charcoal-1);
    font-size: 1.3rem;
    margin: 0;
    letter-spacing: 0;
    transition: color 450ms ease;
  }
  .body p {
    color: var(--clr-charcoal-2);
    font-size: 0.93rem;
    line-height: 1.65;
    margin: 0 0 0.75rem;
    flex: 1;
    transition: color 450ms ease;
  }
  .cta {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--clr-accent-dark);
    font-family: var(--ff-body);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    transition: var(--transition);
    margin-top: auto;
  }
  .cta svg {
    font-size: 0.7rem;
  }

  @media (min-width: 720px) {
    .partners-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
`;

export default Contact;
